import { useEffect } from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { DevTools } from 'jotai-devtools'
import { useAtomValue, useSetAtom } from 'jotai'
import 'jotai-devtools/styles.css'
import './devtools.css'

import { OverlayRoot } from './components/OverlayRoot'
import { BreadcrumbsProvider } from './hooks/useBreadcrumbs'
import { session, websocket } from './store'
import { AuthenticationLayout } from './layouts/AuthenticationLayout'
import { PrimaryLayout } from './layouts/PrimaryLayout'
import { StaffLayout } from './layouts/StaffLayout'
import { ForgotPasswordView } from './views/ForgotPasswordView'
import { LoginView } from './views/LoginView'
import { LogoutView } from './views/LogoutView'
import { MatchChatView } from './views/MatchChatView'
import { MatchDetailView } from './views/MatchDetailView'
import { MatchesView } from './views/MatchesView'
import { OnboardingView } from './views/OnboardingView'
import { PrivacyPolicyView } from './views/PrivacyPolicyView'
import { ProfilePhotosView } from './views/ProfilePhotosView'
import { ProfileView } from './views/ProfileView'
import { SettingsView } from './views/SettingsView'
import { RegisterView } from './views/RegisterView'
import { ResetPasswordView } from './views/ResetPasswordView'
import { TermsOfUseView } from './views/TermsOfUseView'
import { VerifyEmailView } from './views/VerifyEmailView'
import { WaitlistActivationView } from './views/WaitlistActivationView'
import { StaffLoginView } from './views/staff/StaffLoginView'
import { StaffDashboardView } from './views/staff/StaffDashboardView'
import { StaffModerationLayout } from './views/staff/moderation/StaffModerationLayout'
import { StaffUsersView } from './views/staff/moderation/StaffUsersView'
import { StaffPhotosView } from './views/staff/moderation/StaffPhotosView'
import { StaffReportsView } from './views/staff/moderation/StaffReportsView'
import { StaffSettingsView } from './views/staff/settings/StaffSettingsView'
import { StaffSettingsRegionsView } from './views/staff/settings/StaffSettingsRegionsView'
import { StaffSettingsWaitlistView } from './views/staff/settings/StaffSettingsWaitlistView'
import { StaffSettingsStaffView } from './views/staff/settings/StaffSettingsStaffView'
import { StaffSettingsAccountView } from './views/staff/settings/StaffSettingsAccountView'

/**
 * Route tree follows React Router data-mode layout patterns:
 * pathless layout routes + relative child segments (not absolute `/login` paths).
 * @see https://reactrouter.com/start/data/routing#layout-routes
 */
const router = createBrowserRouter([
  {
    path: 'logout',
    element: <LogoutView />,
  },
  {
    element: <AuthenticationLayout />,
    children: [
      { path: 'login', element: <LoginView /> },
      { path: 'register', element: <RegisterView /> },
      { path: 'forgot-password', element: <ForgotPasswordView /> },
      { path: 'reset-password', element: <ResetPasswordView /> },
      { path: 'verify-email', element: <VerifyEmailView /> },
      { path: 'onboarding', element: <OnboardingView /> },
      { path: 'waitlist/activate', element: <WaitlistActivationView /> },
      { path: 'terms', element: <TermsOfUseView /> },
      { path: 'privacy', element: <PrivacyPolicyView /> },
      { path: 'staff/login', element: <StaffLoginView /> },
    ],
  },
  {
    path: 'staff',
    element: <StaffLayout />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <StaffDashboardView /> },
      {
        path: 'moderation',
        element: <StaffModerationLayout />,
        children: [
          { index: true, element: <Navigate to="users" replace /> },
          { path: 'users', element: <StaffUsersView /> },
          { path: 'photos', element: <StaffPhotosView /> },
          { path: 'reports', element: <StaffReportsView /> },
        ],
      },
      { path: 'settings', element: <StaffSettingsView /> },
      { path: 'settings/regions', element: <StaffSettingsRegionsView /> },
      { path: 'settings/waitlist', element: <StaffSettingsWaitlistView /> },
      { path: 'settings/staff', element: <StaffSettingsStaffView /> },
      { path: 'settings/account', element: <StaffSettingsAccountView /> },
    ],
  },
  {
    element: <PrimaryLayout />,
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      { path: 'profile', element: <ProfileView /> },
      { path: 'profile/photos', element: <ProfilePhotosView /> },
      { path: 'settings', element: <SettingsView /> },
      { path: 'matches', element: <MatchesView /> },
      { path: 'matches/:profileId', element: <MatchDetailView /> },
      { path: 'matches/:matchId/chat', element: <MatchChatView /> },
    ],
  },
])

/** Reconnect WS when the tab becomes visible again (browser suspends sockets). */
function WebsocketVisibilityLifecycle() {
  const status = useAtomValue(session.sessionStatusAtom)
  const current = useAtomValue(session.sessionAtom)
  const ensureConnected = useSetAtom(websocket.ensureConnectedAtom)
  const token = current?.token.value

  useEffect(() => {
    if (status !== 'authenticated' || !token) return
    void ensureConnected(token)
  }, [status, token, ensureConnected])

  useEffect(() => {
    if (status !== 'authenticated' || !token) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') void ensureConnected(token)
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [status, token, ensureConnected])

  return null
}

export function Application() {
  return (
    <BreadcrumbsProvider>
      <WebsocketVisibilityLifecycle />
      <DevTools theme="dark" position="bottom-right" />
      <RouterProvider router={router} />
      <OverlayRoot />
    </BreadcrumbsProvider>
  )
}
